import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSchoolReturnsByIdRequest,
  updateSchoolRequest,
  exportBarcodeBooks
} from "../../../../Backend/SchoolsRequests";
import { PackageStatusCell } from "../../../../Components/PackageStatusCell/PackageStatusCell";
import { SchoolStatusCell } from "../../../../Components/SchoolStatusCell/SchoolStatusCell";
import TableTemplate from "../../../../Components/Table/TableTemplate";
import { WebRoutes } from "../../../../Constants";
import { SCHOOL_RETURNS_BY_SCHOOL } from "../../../../Constants/Endpoints";
import { hasValue } from "../../../../Helpers/validations/common";
import { FinalizeModal } from "../FinalizeModal/FinalizeModal";
import { PackageScanModal } from "../PackageScanModal/PackageScanModal";
import "./SchoolPackageReturns.scss";

export function SchoolPackageReturns() {
  const tableTemplateRef = useRef(null);
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showScanModal, setShowScanModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [school, setSchool] = useState({
    id: "",
    year: 0,
    completed: false,
    schoolId: "",
    school: {
      id: "",
      schoolNumber: 0,
      name: "",
      digiDeliveryId: "",
      schoolDeliveryType: "",
      studentCount: 0,
    },
  });

  const defaultScanButton = {
    name: t("Scan"),
    action: () => setShowScanModal(true),
  };
  const defaultCols = [
    {
      name: "barcode",
      headerCaption: t("Barcode"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.firstName",
      filterName: "user.firstName",
      sortName: "user.firstName",
      headerCaption: t("Name"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.preposition",
      filterName: "user.preposition",
      sortName: "user.preposition",
      headerCaption: t("Preposition"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "student.lastName",
      filterName: "user.lastName",
      sortName: "user.lastName",
      headerCaption: t("LastName"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "schoolScanComplete",
      headerCaption: t("Status"),
      textAlign: "left",
      canSort: false,
      canFilter: false,
      canEdit: false,
      caption: (x) => <PackageStatusCell status={x.schoolScanComplete} />,
    },
  ];

  const [columns, setColumns] = useState([]);
  const [finalized, setFinalized] = useState(false);
  const [scanButton, setScanButton] = useState([defaultScanButton]);
  const [defaultManage, setDefaultManage] = useState();

  //#region Hooks
  useEffect(() => {
    getSchoolById();
  }, [id]);

  //#endregion

  //#region Handlers
  const handleSaveButton = async () => {
    if (finalized) {
      const body = {
        id: school.id,
        firstScanCompleted: false,
        secondScanCompleted: false,
        thirdScanCompleted: false,
      };

      const resp = await updateSchoolRequest(body);
      if (resp.success) {
        changeButtons(false);
      }
    } else {
      setShowFinalizeModal(true);
    }
  };

  const handleFinalize = async () => {
    const body = {
      id: school.id,
      firstScanCompleted: true,
      secondScanCompleted: false,
      thirdScanCompleted: false,
    };

    const resp = await updateSchoolRequest(body);
    if (resp.success) {
      setShowFinalizeModal(false);
      getSchoolById();
      tableTemplateRef.current.getItems();
      setShowFinalizeModal(false);
      changeButtons(true);
      setFinalized(true);
    }
  };

  //#endregion

  const changeButtons = (finalized) => {
    if (finalized) {
      setFinalized(true);
      setColumns(defaultCols);
      setScanButton([]);
    } else {
      setFinalized(false);
      setColumns([...defaultCols, defaultManage]);
      setScanButton([defaultScanButton]);
    }
  };

  const getSchoolById = async () => {
    const resp = await getSchoolReturnsByIdRequest(id);

    if (resp.success) {
      const schoolData = resp.data.list[0];
      const defaultManage = {
        name: "manage",
        headerCaption: "",
        canSort: false,
        canFilter: false,
        canEdit: false,
        textAlign: "right",
        caption: (x) => (
          <Button
            key={x.id + "-manage"}
            className="button-manage"
            onClick={() =>
              navigate(
                WebRoutes.PackageReturns.replace(
                  ":schoolId",
                  schoolData.schoolId
                )
                  .replace(":packageId", x.id)
                  .replace(":year", schoolData.year)
              )
            }
          >
            {t("Manage")}
          </Button>
        ),
      };

      if (schoolData.firstScanCompleted) {
        changeButtons(true);
      } else {
        setColumns([...defaultCols, defaultManage]);
      }

      setSchool(schoolData);
      setDefaultManage(defaultManage);
    }
  };
  
  
  async function handleExportOnClick() {
    return await exportBarcodeBooks(school.school.id, school.year);
  }

  return (
    <div className="school-package-returns-page">
      <div className="header-page-buttons-wrapper">
        <div
          className="goback"
          onClick={() => navigate(-1)}
          role="link"
          id={"goback-button"}
        >
          <div className="icon">arrow_back</div>
          <div className="text">{t("GoBack")}</div>
        </div>
        <div className="right-wrapper">
          {school.firstScanCompleted && (
            <div
              className="export-btn"
              title={t("Export")}
              onClick={handleExportOnClick}
            >
              file_download
            </div>
          )}
          {school.firstScanCompleted && (
            <div
              className="summary-btn"
              title={t("Summary")}
              onClick={() =>
                navigate(
                  WebRoutes.SchoolSummary.replace(":id", school.school.id)
                )
              }
            >
              article
            </div>
          )}
          {finalized && (
            <div className="completed">
              <SchoolStatusCell status={school.firstScanCompleted} />
            </div>
          )}
          {!school.secondScanCompleted && (
            <div className="button-save-wrapper">
              <Button
                className="button-save"
                onClick={() => handleSaveButton()}
              >
                {finalized ? t("Reopen") : t("Finalize")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {hasValue(school.schoolId) && (
        <TableTemplate
          title={t("Packages")}
          subtitle={
            <div className={`school-name-wrapper`}>
              <div className={`school-type`}>
                {school.school.schoolDeliveryType}
              </div>
              <div className={`school-name`}>{school.school.name}</div>
            </div>
          }
          ref={tableTemplateRef}
          endpoint={
            SCHOOL_RETURNS_BY_SCHOOL +
            "?schoolId=" +
            school.schoolId +
            "&year=" +
            school.year
          }
          columns={columns}
          uniqueId="schoolNumber"
          paginator={true}
          customButtons={scanButton}
        />
      )}

      {showScanModal && (
        <PackageScanModal
          show={showScanModal}
          school={school}
          onHide={() => setShowScanModal(false)}
        />
      )}

      {showFinalizeModal && (
        <FinalizeModal
          show={showFinalizeModal}
          submit={() => handleFinalize()}
          onHide={() => setShowFinalizeModal(false)}
        />
      )}
    </div>
  );
}
